'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { knowledgeListMachine } from './machines/knowledgeListMachine'
// Mock data removed - use CrmKnowledgeService for real API calls
import type {
  KnowledgeArticle,
  KnowledgeCategory,
  KnowledgeFAQ,
  KnowledgeFilters,
  KnowledgeStats,
  CreateArticleParams,
  UpdateArticleParams,
  CreateCategoryParams,
  UpdateCategoryParams,
  CreateFAQParams,
  UpdateFAQParams,
} from './types'

export type KnowledgeListMachineApi = ReturnType<typeof useMachine<typeof knowledgeListMachine>>

const KnowledgeListMachineContext = createContext<KnowledgeListMachineApi | null>(null)

export function KnowledgeListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(knowledgeListMachine)
  return (
    <KnowledgeListMachineContext.Provider value={api}>
      {children}
    </KnowledgeListMachineContext.Provider>
  )
}

export function useKnowledgeListMachine() {
  const ctx = useContext(KnowledgeListMachineContext)
  if (!ctx) {
    throw new Error('useKnowledgeListMachine must be used within KnowledgeListMachineProvider')
  }
  return ctx
}

export function useKnowledgeListMachineOptional() {
  return useContext(KnowledgeListMachineContext)
}

export function useKnowledgeActions() {
  const machineApi = useKnowledgeListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setCategories: (categories: KnowledgeCategory[]) =>
      send({ type: 'SET_CATEGORIES', categories }),
    addCategory: (category: KnowledgeCategory) => send({ type: 'ADD_CATEGORY', category }),
    updateCategory: (id: string, updates: Partial<KnowledgeCategory>) =>
      send({ type: 'UPDATE_CATEGORY', id, updates }),
    removeCategory: (id: string) => send({ type: 'REMOVE_CATEGORY', id }),
    setArticles: (articles: KnowledgeArticle[]) => send({ type: 'SET_ARTICLES', articles }),
    addArticle: (article: KnowledgeArticle) => send({ type: 'ADD_ARTICLE', article }),
    updateArticle: (id: string, updates: Partial<KnowledgeArticle>) =>
      send({ type: 'UPDATE_ARTICLE', id, updates }),
    removeArticle: (id: string) => send({ type: 'REMOVE_ARTICLE', id }),
    incrementViewCount: (id: string) => send({ type: 'INCREMENT_VIEW_COUNT', id }),
    submitFeedback: (params: { articleId: string; isHelpful: boolean; comment?: string }) =>
      send({ type: 'SUBMIT_FEEDBACK', params }),
    setFAQs: (faqs: KnowledgeFAQ[]) => send({ type: 'SET_FAQS', faqs }),
    addFAQ: (faq: KnowledgeFAQ) => send({ type: 'ADD_FAQ', faq }),
    updateFAQ: (id: string, updates: Partial<KnowledgeFAQ>) =>
      send({ type: 'UPDATE_FAQ', id, updates }),
    removeFAQ: (id: string) => send({ type: 'REMOVE_FAQ', id }),
    search: (query: string) => send({ type: 'SEARCH', query }),
    clearSearch: () => send({ type: 'CLEAR_SEARCH' }),
    setSelectedCategory: (id: string | null) => send({ type: 'SET_SELECTED_CATEGORY', id }),
    setSelectedArticle: (id: string | null) => send({ type: 'SET_SELECTED_ARTICLE', id }),
    setFilters: (filters: Partial<KnowledgeFilters>) => send({ type: 'SET_FILTERS', filters }),
    resetFilters: () => send({ type: 'RESET_FILTERS' }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
    setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
    setStats: (stats: KnowledgeStats) => send({ type: 'SET_STATS', stats }),
    refreshStats: () => send({ type: 'REFRESH_STATS' }),
  }), [send])
}

export function useKnowledgeListStoreCompat() {
  const machineApi = useKnowledgeListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context

    const createArticle = async (params: CreateArticleParams) => {
      const newArticle: KnowledgeArticle = {
        id: `art-${Date.now()}`,
        title: params.title,
        slug: params.title.toLowerCase().replace(/\s+/g, '-'),
        content: params.content,
        contentType: params.contentType,
        status: 'draft',
        visibility: params.visibility || 'internal',
        categoryId: params.categoryId,
        tags: params.tags || [],
        authorId: 'current-user',
        authorName: 'Current User',
        viewCount: 0,
        helpfulCount: 0,
        notHelpfulCount: 0,
        excerpt: params.excerpt,
        seoTitle: params.seoTitle,
        seoDescription: params.seoDescription,
        seoKeywords: params.seoKeywords,
        relatedArticleIds: params.relatedArticleIds,
        createdAt: new Date().toISOString(),
      }
      send({ type: 'ADD_ARTICLE', article: newArticle })
      send({ type: 'REFRESH_STATS' })
      return newArticle
    }

    const updateArticleAsync = async (id: string, params: UpdateArticleParams) => {
      send({
        type: 'UPDATE_ARTICLE',
        id,
        updates: { ...params, updatedAt: new Date().toISOString() },
      })
      const article = machineApi[0].context.articles.find((a) => a.id === id)
      if (!article) throw new Error('Article not found')
      return article
    }

    const deleteArticle = async (id: string) => {
      send({ type: 'REMOVE_ARTICLE', id })
      send({ type: 'REFRESH_STATS' })
    }

    const publishArticle = async (id: string) => {
      send({
        type: 'UPDATE_ARTICLE',
        id,
        updates: {
          status: 'published',
          publishedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      })
    }

    const archiveArticle = async (id: string) => {
      send({
        type: 'UPDATE_ARTICLE',
        id,
        updates: { status: 'archived', updatedAt: new Date().toISOString() },
      })
    }

    const createCategory = async (params: CreateCategoryParams) => {
      const order = machineApi[0].context.categories.length + 1
      const newCategory: KnowledgeCategory = {
        id: `cat-${Date.now()}`,
        name: params.name,
        slug: params.name.toLowerCase().replace(/\s+/g, '-'),
        description: params.description,
        icon: params.icon,
        color: params.color,
        parentId: params.parentId,
        order,
        articleCount: 0,
        isActive: true,
        createdAt: new Date().toISOString(),
      }
      send({ type: 'ADD_CATEGORY', category: newCategory })
      return newCategory
    }

    const updateCategoryAsync = async (id: string, params: UpdateCategoryParams) => {
      send({
        type: 'UPDATE_CATEGORY',
        id,
        updates: { ...params, updatedAt: new Date().toISOString() },
      })
      const category = machineApi[0].context.categories.find((c) => c.id === id)
      if (!category) throw new Error('Category not found')
      return category
    }

    const deleteCategory = async (id: string) => {
      send({ type: 'REMOVE_CATEGORY', id })
    }

    const createFAQ = async (params: CreateFAQParams) => {
      const order = params.order ?? machineApi[0].context.faqs.length + 1
      const newFAQ: KnowledgeFAQ = {
        id: `faq-${Date.now()}`,
        question: params.question,
        answer: params.answer,
        categoryId: params.categoryId,
        tags: params.tags || [],
        order,
        viewCount: 0,
        helpfulCount: 0,
        status: 'draft',
        createdAt: new Date().toISOString(),
      }
      send({ type: 'ADD_FAQ', faq: newFAQ })
      return newFAQ
    }

    const updateFAQAsync = async (id: string, params: UpdateFAQParams) => {
      send({
        type: 'UPDATE_FAQ',
        id,
        updates: { ...params, updatedAt: new Date().toISOString() },
      })
      const faq = machineApi[0].context.faqs.find((f) => f.id === id)
      if (!faq) throw new Error('FAQ not found')
      return faq
    }

    const deleteFAQ = async (id: string) => {
      send({ type: 'REMOVE_FAQ', id })
    }

    const initialize = async () => {
      send({ type: 'SET_LOADING', loading: true })
      // Data will be populated via real API calls from CrmKnowledgeService
      // Initialize with empty arrays - components should call setCategories/setArticles/setFAQs
      // when data is fetched from the API
      send({ type: 'SET_CATEGORIES', categories: [] })
      send({ type: 'SET_ARTICLES', articles: [] })
      send({ type: 'SET_FAQS', faqs: [] })
      send({ type: 'SET_LOADING', loading: false })
      send({ type: 'REFRESH_STATS' })
    }

    return {
      categories: ctx.categories,
      articles: ctx.articles,
      faqs: ctx.faqs,
      searchResults: ctx.searchResults,
      stats: ctx.stats,
      selectedCategoryId: ctx.selectedCategoryId,
      selectedArticleId: ctx.selectedArticleId,
      filters: ctx.filters,
      isLoading: ctx.isLoading,
      isSearching: ctx.isSearching,
      error: ctx.error,
      setCategories: (categories: KnowledgeCategory[]) =>
        send({ type: 'SET_CATEGORIES', categories }),
      addCategory: (category: KnowledgeCategory) => send({ type: 'ADD_CATEGORY', category }),
      updateCategory: (id: string, updates: Partial<KnowledgeCategory>) =>
        send({ type: 'UPDATE_CATEGORY', id, updates }),
      removeCategory: (id: string) => send({ type: 'REMOVE_CATEGORY', id }),
      setArticles: (articles: KnowledgeArticle[]) => send({ type: 'SET_ARTICLES', articles }),
      addArticle: (article: KnowledgeArticle) => send({ type: 'ADD_ARTICLE', article }),
      updateArticle: (id: string, updates: Partial<KnowledgeArticle>) =>
        send({ type: 'UPDATE_ARTICLE', id, updates }),
      removeArticle: (id: string) => send({ type: 'REMOVE_ARTICLE', id }),
      incrementViewCount: (id: string) => send({ type: 'INCREMENT_VIEW_COUNT', id }),
      submitFeedback: (params: { articleId: string; isHelpful: boolean; comment?: string }) =>
        send({ type: 'SUBMIT_FEEDBACK', params }),
      setFAQs: (faqs: KnowledgeFAQ[]) => send({ type: 'SET_FAQS', faqs }),
      addFAQ: (faq: KnowledgeFAQ) => send({ type: 'ADD_FAQ', faq }),
      updateFAQ: (id: string, updates: Partial<KnowledgeFAQ>) =>
        send({ type: 'UPDATE_FAQ', id, updates }),
      removeFAQ: (id: string) => send({ type: 'REMOVE_FAQ', id }),
      search: (query: string) => send({ type: 'SEARCH', query }),
      clearSearch: () => send({ type: 'CLEAR_SEARCH' }),
      setSelectedCategory: (id: string | null) => send({ type: 'SET_SELECTED_CATEGORY', id }),
      setSelectedArticle: (id: string | null) => send({ type: 'SET_SELECTED_ARTICLE', id }),
      setFilters: (filters: Partial<typeof ctx.filters>) => send({ type: 'SET_FILTERS', filters }),
      resetFilters: () => send({ type: 'RESET_FILTERS' }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
      setError: (error: string | null) => send({ type: 'SET_ERROR', error }),
      setStats: (stats: typeof ctx.stats) => send({ type: 'SET_STATS', stats: stats! }),
      refreshStats: () => send({ type: 'REFRESH_STATS' }),
      createArticle,
      updateArticleAsync,
      deleteArticle,
      publishArticle,
      archiveArticle,
      createCategory,
      updateCategoryAsync,
      deleteCategory,
      createFAQ,
      updateFAQAsync,
      deleteFAQ,
      initialize,
    }
  }, [machineApi])
}
